'use client';

import {
  createContext,
  type ReactNode,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { OnboardingProviderProps } from './OnboardingProvider.types';
import { getCookie, setCookie, deleteCookie } from 'cookies-next/client';

// Cookie configuration
const COOKIE_PREFIX = 'SORABAN_HIGHLIGHT_';
const COOKIE_EXPIRY_DATE = new Date();

COOKIE_EXPIRY_DATE.setFullYear(COOKIE_EXPIRY_DATE.getFullYear() + 10);

const OnboardingContext = createContext<OnboardingProviderProps>({
  dismissHighlight: () => null,
  resetHighlights: () => null,
  onNextHighlight: () => null,
  onPreviousHighlight: () => null,
  getHasPreviousHighlight: () => false,
  getHasNextHighlight: () => false,
  registerHighlight: () => null,
  unregisterHighlight: () => null,
  isHighlightActive: () => true,
  isHighlightDismissed: () => false,
  loadHighlightDismissState: () => null,
  isInitialized: false,
});

interface HighlightSet {
  [setId: string]: Array<string>;
}

const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [highlightSets, setHighlightSets] = useState<HighlightSet>({});
  const [activeHighlights, setActiveHighlights] = useState<
    Record<string, string>
  >({});
  const [dismissedHighlights, setDismissedHighlights] = useState<Set<string>>(
    new Set(),
  );
  const [isInitialized, setIsInitialized] = useState(false);

  const getCookieName = useCallback(
    (highlightId: string) => `${COOKIE_PREFIX}${highlightId}`,
    [],
  );

  useEffect(() => {
    const loadDismissedFromCookies = () => {
      const dismissed = new Set<string>();

      setDismissedHighlights(dismissed);
      setIsInitialized(true);
    };

    loadDismissedFromCookies();
  }, []);

  const registerHighlight = useCallback(
    (highlightId: string, setId?: string) => {
      const cookieName = getCookieName(highlightId);
      const isDismissedInCookie = getCookie(cookieName) === 'true';

      if (isDismissedInCookie) {
        setDismissedHighlights((prev) => new Set([...prev, highlightId]));
      }

      if (!setId) return;

      setHighlightSets((prev) => {
        const currentSet = prev[setId] || [];
        if (!currentSet.includes(highlightId)) {
          return {
            ...prev,
            [setId]: [...currentSet, highlightId],
          };
        }
        return prev;
      });

      // Set this highlight as active if no active highlight exists for this set
      // and this highlight is not dismissed
      setActiveHighlights((prev) => {
        if (!prev[setId]) {
          const cookieName = getCookieName(highlightId);
          const isDismissedInCookie = getCookie(cookieName) === 'true';
          const isCurrentlyDismissed =
            dismissedHighlights.has(highlightId) || isDismissedInCookie;

          if (!isCurrentlyDismissed) {
            return { ...prev, [setId]: highlightId };
          }
        }
        return prev;
      });
    },
    [getCookieName, dismissedHighlights],
  );

  const unregisterHighlight = useCallback(
    (highlightId: string, setId?: string) => {
      if (!setId) return;

      setHighlightSets((prev) => ({
        ...prev,
        [setId]: (prev[setId] || []).filter((id) => id !== highlightId),
      }));
    },
    [],
  );

  const onNextHighlight = useCallback(
    (_currentId: string, setId?: string) => {
      if (!setId) return;

      const currentSet = highlightSets[setId] || [];
      const availableHighlights = currentSet.filter(
        (id) => !dismissedHighlights.has(id),
      );
      const currentActive = activeHighlights[setId];
      const currentIndex = availableHighlights.indexOf(currentActive);

      if (currentIndex < availableHighlights.length - 1) {
        const nextHighlight = availableHighlights[currentIndex + 1];
        setActiveHighlights((prev) => ({
          ...prev,
          [setId]: nextHighlight,
        }));
      }
    },
    [highlightSets, activeHighlights, dismissedHighlights],
  );

  const onPreviousHighlight = useCallback(
    (_currentId: string, setId?: string) => {
      if (!setId) return;

      const currentSet = highlightSets[setId] || [];
      const availableHighlights = currentSet.filter(
        (id) => !dismissedHighlights.has(id),
      );
      const currentActive = activeHighlights[setId];
      const currentIndex = availableHighlights.indexOf(currentActive);

      if (currentIndex > 0) {
        const previousHighlight = availableHighlights[currentIndex - 1];
        setActiveHighlights((prev) => ({
          ...prev,
          [setId]: previousHighlight,
        }));
      }
    },
    [highlightSets, activeHighlights, dismissedHighlights],
  );

  const getHasNextHighlight = useCallback(
    (setId?: string) => {
      if (!setId) return false;

      const currentSet = highlightSets[setId] || [];
      const availableHighlights = currentSet.filter(
        (id) => !dismissedHighlights.has(id),
      );
      const currentActive = activeHighlights[setId];
      const currentIndex = availableHighlights.indexOf(currentActive);

      return currentIndex < availableHighlights.length - 1;
    },
    [highlightSets, activeHighlights, dismissedHighlights],
  );

  const getHasPreviousHighlight = useCallback(
    (setId?: string) => {
      if (!setId) return false;

      const currentSet = highlightSets[setId] || [];
      const availableHighlights = currentSet.filter(
        (id) => !dismissedHighlights.has(id),
      );
      const currentActive = activeHighlights[setId];
      const currentIndex = availableHighlights.indexOf(currentActive);

      return currentIndex > 0;
    },
    [highlightSets, activeHighlights, dismissedHighlights],
  );

  const dismissHighlight = useCallback(
    (highlightId: string) => {
      // Find which set this highlight belongs to
      let targetSetId: string | null = null;
      Object.entries(highlightSets).forEach(([setId, highlights]) => {
        if (highlights.includes(highlightId)) {
          targetSetId = setId;
        }
      });

      if (targetSetId) {
        // Dismiss all highlights in the set
        const highlightsInSet = highlightSets[targetSetId] || [];

        // Set cookies for all highlights in the set
        highlightsInSet.forEach((id) => {
          const cookieName = getCookieName(id);
          setCookie(cookieName, 'true', { expires: COOKIE_EXPIRY_DATE });
        });

        setDismissedHighlights((prev) => {
          const newDismissed = new Set([...prev, ...highlightsInSet]);
          return newDismissed;
        });

        // Remove the active highlight for this set
        setActiveHighlights((prev) => {
          const newActive = { ...prev };
          delete newActive[targetSetId!];
          return newActive;
        });
      } else {
        // Fallback to original behavior for highlights not in a set
        const cookieName = getCookieName(highlightId);
        setCookie(cookieName, 'true', { expires: COOKIE_EXPIRY_DATE });

        setDismissedHighlights((prev) => new Set([...prev, highlightId]));
      }
    },
    [getCookieName, highlightSets],
  );

  const resetHighlights = useCallback(() => {
    dismissedHighlights.forEach((highlightId) => {
      const cookieName = getCookieName(highlightId);
      deleteCookie(cookieName);
    });

    setDismissedHighlights(new Set());
  }, [dismissedHighlights, getCookieName]);

  const isHighlightActive = useCallback(
    (highlightId: string, setId?: string) => {
      if (!setId) return true;
      return activeHighlights[setId] === highlightId;
    },
    [activeHighlights],
  );

  const isHighlightDismissed = useCallback(
    (highlightId: string) => {
      return dismissedHighlights.has(highlightId);
    },
    [dismissedHighlights],
  );

  const loadHighlightDismissState = useCallback(
    (highlightId: string) => {
      const cookieName = getCookieName(highlightId);
      const isDismissedInCookie = getCookie(cookieName) === 'true';

      if (isDismissedInCookie) {
        setDismissedHighlights((prev) => new Set([...prev, highlightId]));
      }
    },
    [getCookieName],
  );

  return (
    <OnboardingContext
      value={{
        resetHighlights,
        dismissHighlight,
        onNextHighlight,
        onPreviousHighlight,
        getHasPreviousHighlight,
        getHasNextHighlight,
        registerHighlight,
        unregisterHighlight,
        isHighlightActive,
        isHighlightDismissed,
        loadHighlightDismissState,
        isInitialized,
      }}
    >
      {children}
    </OnboardingContext>
  );
};

export { OnboardingContext, OnboardingProvider };
